import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchArtistByCategory } from "../../redux/slices/artistSlice";
import classes from "./artistsByCategory.module.css";
import Filter from "../../components/introSection/Filter";
import Footer from "../../components/footer";
import { ARTIST_2 } from "../../utils/images";
import ArtistCategories from "../../components/howArtistNetworkWorks";

const matchCategoryLabelFromUrl = (artistCategoryUrl) => {
  if (artistCategoryUrl == "video_editor") {
    return "Video Editor";
  }

  if (artistCategoryUrl == "music_band") {
    return "Music Band";
  }

  if (artistCategoryUrl == "dance_group") {
    return "Dance Group";
  }

  if (artistCategoryUrl == "beat_boxer") {
    return "Beat Boxer";
  }

  return artistCategoryUrl;
};

const matchCategoryMappingFromUrl = (artistCategoryUrl) => {
  if (artistCategoryUrl == "video-editor") {
    return "video_editor";
  }

  if (artistCategoryUrl == "music-band") {
    return "music_band";
  }

  if (artistCategoryUrl == "dance-group") {
    return "dance_group";
  }

  if (artistCategoryUrl == "beat-boxer") {
    return "beat_boxer";
  }

  if (artistCategoryUrl == "photographer") {
    return "photography";
  }

  return artistCategoryUrl;
};

const ArtistsByCategory = ({ category }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    artists,
    loading,
    error,
    totalArtistsByCategory: totalCount,
  } = useSelector((state) => state.artists);
  const [isDesktop, setIsDesktop] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const totalPages = Math.ceil(totalCount / pageSize);

  const { artistCategoryUrl } = useParams();
  const artistCategory =
    category || matchCategoryMappingFromUrl(artistCategoryUrl);

  useEffect(() => {
    const checkWindowSize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    checkWindowSize();
    window.addEventListener("resize", checkWindowSize);

    return () => window.removeEventListener("resize", checkWindowSize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArtists();
  }, [dispatch, artistCategory, currentPage, pageSize]);

  const fetchArtists = () => {
    dispatch(
      fetchArtistByCategory({
        category: artistCategory,
        page_number: currentPage,
        page_size: pageSize,
        sortBy: "createdAt:desc",
      })
    );
  };

  const handleViewProfile = (username) => {
    navigate(`/profile/${username}`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const getCategoryTitle = () => {
    const titles = {
      singer: "Hire the best Singer for your event!",
      dancer: "Find top Dancers for your special occasion!",
      comedian: "Book hilarious Comedians for your celebration!",
      magician: "Find amazing Magicians for your event!",
      musician: "Get talented Musicians to enhance your event!",
      "music-band": "Book an amazing Music Band for your party!",
      dj: "Hire a DJ to rock your event!",
      "beat-boxer": "Find talented Beat Boxers for your next show!",
      photographer: "Hire the best Photographers to capture your moments!",
      "video-editor": "Get expert Video Editors for your projects!",
      "dance-group": "Book a Dance Group for your event!",
      speaker: "Find inspiring Speakers for your conferences!",
      rapper: "Get talented Rappers for your event!",
    };

    return (
      titles[artistCategory] ||
      `Discover amazing ${artistCategory}s for your next event!`
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 12 },
    },
  };

  return (
    <>
      <div className={classes.pageContainer}>
        <motion.div
          className={classes.headerSection}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className={classes.title}>{getCategoryTitle()}</h1>

          <Filter
            dimensions={[
              {
                key: "location",
                label: "Location",
                options: [
                  { value: "delhi", label: "Delhi" },
                  { value: "mumbai", label: "Mumbai" },
                  { value: "bengaluru", label: "Bengaluru" },
                  { value: "hyderabad", label: "Hyderabad" },
                  { value: "chennai", label: "Chennai" },
                ],
              },
              // {
              //   key: "priceRange",
              //   label: "Budget",
              //   options: [
              //     { value: "1000-2000", label: "₹1,000-2,000" },
              //     { value: "2000-5000", label: "₹2,000-5,000" },
              //     { value: "5000-10000", label: "₹5,000-10,000" },
              //     { value: "10000+", label: "₹10,000+" },
              //   ],
              // },
              {
                key: "experience_years",
                label: "Experience",
                options: [
                  {
                    value: JSON.stringify({ min: 1, max: 3 }),
                    label: "1-3 years",
                  },
                  {
                    value: JSON.stringify({ min: 3, max: 5 }),
                    label: "3-5 years",
                  },
                  {
                    value: JSON.stringify({ min: 5, max: 10 }),
                    label: "5-10 years",
                  },
                  {
                    value: JSON.stringify({ min: 10, max: 1000 }),
                    label: "10+ years",
                  },
                ],
              },
            ]}
            category={artistCategory}
          />
        </motion.div>

        <div className={classes.divider}></div>

        {loading ? (
          <div className={classes.loadingContainer}>
            <div className={classes.pulseDot}></div>
            <div className={classes.pulseDot}></div>
            <div className={classes.pulseDot}></div>
            <p>
              Finding amazing {matchCategoryLabelFromUrl(artistCategory)}s for
              you...
            </p>
          </div>
        ) : error ? (
          <div className={classes.errorContainer}>
            <p>
              Sorry, we couldn't load artists at this time. Please try again
              later.
            </p>
          </div>
        ) : artists?.length > 0 ? (
          <>
            <motion.div
              className={classes.artistsGrid}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {artists.map((artist, index) => (
                <motion.div
                  key={artist.id || index}
                  className={classes.artistCard}
                  variants={cardVariants}
                  whileHover={{
                    y: -10,
                    boxShadow: "0 10px 25px rgba(124, 8, 171, 0.2)",
                    transition: { duration: 0.3 },
                  }}
                  onClick={() => handleViewProfile(artist.username)}
                >
                  <div
                    className={classes.imageContainer}
                    style={{
                      background: artist?.profile_picture
                        ? `url(${artist?.profile_picture})`
                        : `url(${ARTIST_2})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    {/* <img
                      src={artist?.profile_picture || ARTIST_2}
                      alt={artist.name}
                    /> */}
                    <div className={classes.gradient}></div>
                  </div>

                  <div className={classes.artistInfo}>
                    <h3 className={classes.artistName}>
                      {`${artist?.first_name} ${artist?.last_name}` ||
                        "Artist Name"}
                    </h3>

                    <div className={classes.details}>
                      <div className={classes.detail}>
                        <span className={classes.detailIcon}>📍</span>
                        <span>
                          {artist?.location?.length > 1
                            ? `${artist?.location[0]} +${artist?.location?.length} more...`
                            : artist?.location?.[0] || "Noida"}
                        </span>
                      </div>

                      <div className={classes.detail}>
                        <span className={classes.detailIcon}>🎭</span>
                        <span>
                          {artist?.genre ||
                            matchCategoryLabelFromUrl(artistCategory)}
                        </span>
                      </div>

                      <div className={classes.detail}>
                        <span className={classes.detailIcon}>⏳</span>
                        <span>
                          {`${
                            artist?.experience_years?.max !== undefined
                              ? `${artist?.experience_years?.max} years`
                              : "Not Specified"
                          }` || "5 Years"}
                        </span>
                      </div>

                      <div className={classes.detail}>
                        <span className={classes.detailIcon}>⭐</span>
                        <span>{artist?.rating || "No ratings yet"}</span>
                      </div>
                    </div>

                    <motion.button
                      className={classes.bookButton}
                      onClick={() => handleViewProfile(artist.username)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={true}
                    >
                      Book Now Coming Soon
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={classes.paginationContainer}>
                <button
                  className={`${classes.paginationButton} ${classes.paginationArrow}`}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>

                {/* Render page buttons */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      className={`${classes.paginationButton} ${
                        currentPage === pageNum ? classes.activePage : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className={classes.paginationEllipsis}>...</span>
                    <button
                      className={classes.paginationButton}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  className={`${classes.paginationButton} ${classes.paginationArrow}`}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={classes.emptyContainer}>
            <div className={classes.emptyIcon}>🔍</div>
            <p>
              No {matchCategoryLabelFromUrl(artistCategory)}s found matching
              your criteria
            </p>
            <button
              className={classes.resetButton}
              onClick={() => {
                setCurrentPage(1);
                dispatch(
                  fetchArtistByCategory({
                    category: artistCategory || "dancer",
                    page_number: 1,
                    page_size: pageSize,
                  })
                );
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
      <ArtistCategories />
      <Footer />
    </>
  );
};

export default ArtistsByCategory;
